/* eslint-disable jsx-a11y/alt-text */
import { useCallback, useEffect, useMemo } from "react"

import * as turf from "@turf/turf"
import * as htmlToImage from "html-to-image"
import * as ReactDOMClient from "react-dom/client"
import { useMatch, useParams } from "react-router-dom"

import { DEFAULT_SIGN_ICON } from "@l2r-front/l2r-common"
import { Source, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworkReferential } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"
import { useUrlParams } from "@l2r-front/l2r-utils"

import { OBJECT_TYPE_FILTER } from "../../constants/filters"
import { APP_DOCUMENT, MODULE_DOCUMENTS, ModulesInfo, NETWORK_ATTACHMENT, ROAD_ATTACHMENT, SECTION_ATTACHMENT } from "../../constants/modules"
import { DOCUMENTS_COUNT_PER_PAGE } from "../../constants/pages"
import { NETWORK_DOCUMENTS_PAGE, ROAD_DOCUMENTS_PAGE } from "../../constants/urlParams"
import { useDocumentStateContext } from "../../contexts/DocumentContext"
import { useDocuments } from "../../hooks/queries/useDocument"

const ICON_BORDER_WIDTH = 4

export function DocumentsSource(props) {

    const {
        iconOffset,
        ...otherProps
    } = props

    const ICON_SIZE = 45
    const ICON_PADDING = 4
    const { attachmentId, road } = useParams()
    const matchNetwork = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/attached/${NETWORK_ATTACHMENT}`)
    const matchDetail = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/*`)
    const { getParam } = useUrlParams()
    const pageUrlParam = (matchDetail || matchNetwork) ? null : road ? ROAD_DOCUMENTS_PAGE : NETWORK_DOCUMENTS_PAGE
    const { getMapRef } = useMapDispatchContext()
    const theme = useTheme()
    const { apiFilters } = useDocumentStateContext()
    const matchSection = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/${SECTION_ATTACHMENT}`)
    const match = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/:attachmentType/*`)
    const attachmentType = matchNetwork ? NETWORK_ATTACHMENT : matchSection ? SECTION_ATTACHMENT : match ? match.params.attachmentType : null

    const page = useMemo(() => {
        return parseInt(getParam(pageUrlParam)) || 1
    }, [getParam, pageUrlParam])

    const { data } = useDocuments(
        {
            as_geojson: true,
            ...(road && { linear_location_road: road }),
            ...(!attachmentId && { offset: (page - 1) * DOCUMENTS_COUNT_PER_PAGE }),
            ...(!attachmentId && { limit: DOCUMENTS_COUNT_PER_PAGE }),
            ...(!!attachmentId && { attached_object_uuid: attachmentId }),
            ...(!!matchNetwork && { [OBJECT_TYPE_FILTER]: NETWORK_ATTACHMENT }),
            ...(apiFilters && { ...apiFilters }),
            ...((attachmentType === SECTION_ATTACHMENT || attachmentType === ROAD_ATTACHMENT) && { [OBJECT_TYPE_FILTER]: attachmentType.toUpperCase() }),
        })

    const { data: referential } = useNetworkReferential()

    const createMapIcon = useCallback(async (type, map, theme) => {
        const color = theme.palette[ModulesInfo?.[type]?.color]?.main
        const Icon = ModulesInfo?.[type]?.icon
        const iconId = type

        const rootContainer = document.createElement("div")
        Object.assign(rootContainer.style, {
            boxSizing: "border-box",
            position: "absolute",
            top: "-9999px",
            left: "-9999px",
            width: ICON_SIZE - ICON_PADDING + "px",
            height: ICON_SIZE - ICON_PADDING + "px",
        })
        document.body.appendChild(rootContainer)

        const root = ReactDOMClient.createRoot(rootContainer)

        root.render(
            <div style={{
                borderRadius: "50%",
                boxSizing: "border-box",
                height: "100%",
                overflow: "hidden",
                position: "relative",
                padding: `${ICON_PADDING}px`,
                width: "100%",
            }}>
                <div
                    style={{
                        backgroundColor: "white",
                        borderRadius: "50%",
                        boxShadow: theme.shadows[31],
                        position: "absolute",
                        inset: 0,
                    }}
                />
                <div
                    style={{
                        backgroundColor: color,
                        borderRadius: "50%",
                        position: "absolute",
                        top: ICON_BORDER_WIDTH,
                        left: ICON_BORDER_WIDTH,
                        width: `calc(100% - ${2 * ICON_BORDER_WIDTH}px)`,
                        height: `calc(100% - ${2 * ICON_BORDER_WIDTH}px)`,
                    }}
                />
                {Icon ? (
                    <Icon
                        style={{
                            width: `calc(100% - ${iconOffset}px)`,
                            height: `calc(100% - ${iconOffset}px)`,
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            objectFit: "contain",
                            color: "#fff",
                        }}
                    />
                ) : (
                    <img
                        src={DEFAULT_SIGN_ICON}
                        style={{
                            width: `calc(100% - ${iconOffset}px)`,
                            height: `calc(100% - ${iconOffset}px)`,
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            objectFit: "contain",
                        }}
                    />
                )}
            </div>,
        )

        // Wait a tick so React flushes render
        await new Promise(r => setTimeout(r, 0))

        try {
            const dataUrl = await htmlToImage.toPng(rootContainer.firstChild, {
                height: ICON_SIZE,
                width: ICON_SIZE,
                pixelRatio: 1,
                skipFonts: true,
            })

            window.generatedIcons[iconId] = dataUrl
            map.loadImage(dataUrl, (_, icon) => {
                if (!map.hasImage(iconId)) {
                    map.addImage(iconId, icon)
                }
            })
        } catch (e) {
            console.error("Error generating icon", e)
        } finally {
            root.unmount()
            document.body.removeChild(rootContainer)
        }
    }, [iconOffset])

    useEffect(() => {
        const map = getMapRef()?.getMap()

        if (!map || !theme) {
            return
        }

        if (!window.generatedIcons) {
            window.generatedIcons = {}
        }
        Object.keys(ModulesInfo).forEach(documentType => {
            if (!window.generatedIcons[documentType]) {
                createMapIcon(documentType, map, theme)
            }
        })

        const loadIcon = (e) => {
            const type = e.id

            if (!type) {
                return
            }

            if (!window.generatedIcons) {
                window.generatedIcons = {}
            }

            if (window.generatedIcons[type]) {
                map.loadImage(
                    window.generatedIcons[type],
                    (error, icon) => {
                        if (error) {
                            return console.error(error)
                        }

                        if (!map.hasImage(type)) {
                            map.addImage(type, icon)
                        }
                    },
                )
            } else {
                createMapIcon(type, map, theme)
            }
        }

        map?.on("styleimagemissing", loadIcon)

        return () => map?.off("styleimagemissing", loadIcon)

    }, [createMapIcon, getMapRef, theme])

    const sanitizedData = useMemo(() => {
        const features = data?.features || data?.results
        if (!features || !referential) {
            return null
        }
        const seenUuids = new Set()
        const sanitizedFeatures = []
        features.forEach((feature) => {
            if (feature.properties.attached_object_uuid && seenUuids.has(feature.properties.attached_object_uuid)) {
                return
            }
            seenUuids.add(feature.properties.attached_object_uuid)

            const referentialRoadGeometries = getReferentialRoadGeometries(referential, feature)

            const finalGeometry =
                feature.geometry.type !== "Point"
                    ? findNearestPointOnRoad(feature, referentialRoadGeometries)
                    : feature.geometry

            sanitizedFeatures.push({
                id: feature.id,
                geometry: finalGeometry,
                properties: {
                    ...feature.properties,
                    geometry: finalGeometry,
                    icon: feature.properties.attached_object_content_type ?? feature.properties.linear_location_type.toLowerCase(),
                    linearLocation: feature.linear_location,
                    linear_location_type: feature.properties.linear_location_type?.toLowerCase(),
                    uuid: feature.id,
                },
            })
        })

        return turf.featureCollection(sanitizedFeatures)
    }, [data, referential])

    return <Source {...otherProps} type="geojson" data={sanitizedData} tolerance={.001} />

}

DocumentsSource.defaultProps = {
    iconOffset: 20,
}


const getReferentialRoadGeometries = (referential, feature) => {
    return (
        referential.features
            .filter(road => road.linear_location[0].road === feature?.linear_location?.[0]?.road)
            .map(road => road.geometry) || []
    )
}
const findNearestPointOnRoad = (feature, roadGeometries) => {
    if (!roadGeometries.length) {
        return turf.centroid(feature).geometry
    }

    const centroid = turf.centroid(feature).geometry
    const lines = roadGeometries.map(g => turf.lineString(g.coordinates))
    const combined = turf.combine(turf.featureCollection(lines))
    const multiLine = combined.features[0]

    return turf.nearestPointOnLine(multiLine, centroid).geometry
}